import { User } from 'firebase/auth'
import { v4 as uuidv4 } from 'uuid'
import {
  DriverColumnMapping,
  DriverUpdateRow,
  DriverBulkUploadResult,
  FieldConflict,
  FieldConflictResolution,
  UnmatchedDriverAction
} from '@/types'
import type {
  DriverMaster,
  CreateDriverMasterData,
  UpdateDriverMasterData
} from '@/types/driverMaster'
import { FirestoreService } from './firestoreService'
import { DriverMasterService } from './driverMasterService'

type ConflictResolutionMap = Map<string, FieldConflictResolution | FieldConflictResolution[]>
type UnmatchedActionMap = Map<string, UnmatchedDriverAction>

const DRIVER_IMPORT_PLATFORM = 'driver_import'

export class DriverBulkUploadService {
  private user: User
  private firestoreService: FirestoreService

  constructor(user: User) {
    this.user = user
    this.firestoreService = new FirestoreService(user)
  }

  /**
   * Extract driver data from raw CSV rows using the provided column mapping.
   */
  async extractDriverData(
    stagingId: string,
    columnMapping: DriverColumnMapping
  ): Promise<DriverUpdateRow[]> {
    const staging = await this.firestoreService.getDriverUploadStaging(stagingId)
    if (!staging) {
      throw new Error('Driver upload staging not found')
    }

    if (!Array.isArray(staging.rawData) || staging.rawData.length === 0) {
      throw new Error('Inga data kunde läsas från filen')
    }

    const existingRowIds = new Map<number, string>()
    ;(staging.driverUpdateRows || []).forEach(row => {
      existingRowIds.set(row.rowIndex, row.rowId)
    })

    const driverRows: DriverUpdateRow[] = staging.rawData.map((row, index) => {
      const normalizedRow = this.buildNormalizedRow(row)
      const rowId = existingRowIds.get(index) || uuidv4()
      const identifiers = this.extractIdentifiers(row, columnMapping, normalizedRow)
      const profileData = this.extractProfileData(row, columnMapping, normalizedRow)

      return {
        rowId,
        rowIndex: index,
        identifiers,
        profileData,
        hasMatch: false,
        conflicts: []
      }
    })

    await this.firestoreService.updateDriverUploadStaging(stagingId, {
      columnMapping,
      driverUpdateRows: driverRows,
      status: 'matching',
      processingProgress: null,
      uploadResult: null,
      error: null
    })

    return driverRows
  }

  /**
   * Match drivers and detect conflicts for mapped rows.
   */
  async matchAndDetectConflicts(
    stagingId: string,
    driverRows: DriverUpdateRow[]
  ): Promise<{
    driverUpdateRows: DriverUpdateRow[]
    availableDrivers: DriverMaster[]
  }> {
    if (driverRows.length === 0) {
      return { driverUpdateRows: [], availableDrivers: [] }
    }

    const availableDrivers = await DriverMasterService.getDrivers(this.user)
    const driverIndex = new Map(availableDrivers.map(driver => [driver.id, driver]))

    const updatedRows: DriverUpdateRow[] = []

    for (const row of driverRows) {
      const clonedRow: DriverUpdateRow = {
        ...row,
        identifiers: { ...row.identifiers },
        profileData: { ...row.profileData },
        conflicts: []
      }

      const match = await DriverMasterService.searchDriversAliases(
        this.user,
        DRIVER_IMPORT_PLATFORM,
        {
          uuid: clonedRow.identifiers.uuid,
          email: clonedRow.identifiers.email,
          phone: clonedRow.identifiers.phone,
          name: clonedRow.identifiers.name,
          personalCode: clonedRow.identifiers.personalCode,
          taxiCode: clonedRow.identifiers.taxiCode,
          employeeNumber: clonedRow.identifiers.employeeNumber
        }
      )

      if (match) {
        clonedRow.hasMatch = true
        clonedRow.matchedDriverId = match.driverId
        clonedRow.matchedDriverName = match.driverName
        clonedRow.matchType = match.fuzzyMatch ? 'fuzzy' : 'exact'
        clonedRow.matchScore = match.matchScore

        const existingDriver = driverIndex.get(match.driverId)
        if (existingDriver) {
          clonedRow.conflicts = this.detectConflicts(existingDriver, clonedRow)
        }
      } else {
        clonedRow.hasMatch = false
        clonedRow.matchedDriverId = undefined
        clonedRow.matchedDriverName = undefined
        clonedRow.matchType = undefined
        clonedRow.matchScore = undefined
        clonedRow.conflicts = []
      }

      updatedRows.push(clonedRow)
    }

    // Flag rows that share the same matched driver (duplicate matches)
    const matchedDriverIdCounts = new Map<string, number>()
    for (const row of updatedRows) {
      if (row.matchedDriverId) {
        matchedDriverIdCounts.set(row.matchedDriverId, (matchedDriverIdCounts.get(row.matchedDriverId) ?? 0) + 1)
      }
    }
    for (const row of updatedRows) {
      if (row.matchedDriverId && (matchedDriverIdCounts.get(row.matchedDriverId) ?? 0) > 1) {
        row.isDuplicateMatch = true
      }
    }

    await this.firestoreService.updateDriverUploadStaging(stagingId, {
      driverUpdateRows: updatedRows,
      status: 'reviewing'
    })

    return {
      driverUpdateRows: updatedRows,
      availableDrivers
    }
  }

  /**
   * Process driver updates: apply conflict resolutions and unmatched driver actions.
   */
  async processDriverUpdates(
    stagingId: string,
    driverRows: DriverUpdateRow[],
    conflictResolutions: ConflictResolutionMap,
    unmatchedActions: UnmatchedActionMap,
    onProgress?: (progress: {
      current: number
      total: number
      percentage: number
      status: string
      estimatedTimeRemaining?: number
    }) => void
  ): Promise<DriverBulkUploadResult> {
    if (driverRows.length === 0) {
      return {
        totalRows: 0,
        driversCreated: 0,
        driversUpdated: 0,
        driversSkipped: 0,
        fieldsUpdated: 0,
        errors: []
      }
    }

    await this.firestoreService.updateDriverUploadStaging(stagingId, {
      status: 'processing',
      processingProgress: {
        current: 0,
        total: driverRows.length,
        percentage: 0,
        status: 'Startar bearbetning'
      },
      uploadResult: null,
      error: null
    })

    const availableDrivers = await DriverMasterService.getDrivers(this.user)
    const driverMap = new Map(availableDrivers.map(driver => [driver.id, driver]))

    const startTime = Date.now()
    let processed = 0

    let driversCreated = 0
    let driversUpdated = 0
    let driversSkipped = 0
    let fieldsUpdated = 0
    const errors: DriverBulkUploadResult['errors'] = []
    const processedRows: DriverUpdateRow[] = []

    for (const row of driverRows) {
      processed += 1
      const progress = this.buildProgress(startTime, processed, driverRows.length)

      try {
        const clonedRow: DriverUpdateRow = {
          ...row,
          identifiers: { ...row.identifiers },
          profileData: { ...row.profileData },
          conflicts: row.conflicts?.map(conflict => ({ ...conflict })) || []
        }

        const resolutionEntries = conflictResolutions.get(clonedRow.rowId)
        const resolutionList = Array.isArray(resolutionEntries)
          ? resolutionEntries
          : resolutionEntries
            ? [resolutionEntries]
            : []
        const resolutionByField = new Map(
          resolutionList.map(resolution => [resolution.fieldName, resolution])
        )

        const unmatchedAction = unmatchedActions.get(clonedRow.rowId)

        if (!clonedRow.hasMatch) {
          if (!unmatchedAction || unmatchedAction.action === 'skip') {
            driversSkipped += 1
            processedRows.push(clonedRow)
            continue
          }

          if (unmatchedAction.action === 'create_new') {
            const createData = this.buildCreateData(clonedRow, unmatchedAction)
            const createdDriver = await DriverMasterService.createDriver(this.user, createData)
            driversCreated += 1
            fieldsUpdated += this.countDefinedFields(createData)

            clonedRow.hasMatch = true
            clonedRow.matchedDriverId = createdDriver.id
            clonedRow.matchedDriverName = createdDriver.primaryName
            clonedRow.matchType = 'exact'
            clonedRow.matchScore = 100
            clonedRow.conflicts = []

            driverMap.set(createdDriver.id, createdDriver)
            processedRows.push(clonedRow)
            continue
          }

          if (
            unmatchedAction.action === 'select_existing' &&
            unmatchedAction.selectedDriverId
          ) {
            clonedRow.hasMatch = true
            clonedRow.matchedDriverId = unmatchedAction.selectedDriverId
            const existingDriver = driverMap.get(unmatchedAction.selectedDriverId)

            if (existingDriver) {
              clonedRow.matchedDriverName = existingDriver.primaryName
              clonedRow.conflicts = this.detectConflicts(existingDriver, clonedRow)
            } else {
              const fetchedDriver = await DriverMasterService.getDriver(
                this.user,
                unmatchedAction.selectedDriverId
              )
              if (!fetchedDriver) {
                throw new Error('Vald förare kunde inte hämtas')
              }
              driverMap.set(fetchedDriver.id, fetchedDriver)
              clonedRow.matchedDriverName = fetchedDriver.primaryName
              clonedRow.conflicts = this.detectConflicts(fetchedDriver, clonedRow)
            }
          } else {
            driversSkipped += 1
            processedRows.push(clonedRow)
            continue
          }
        }

        const targetDriverId = clonedRow.matchedDriverId
        if (!targetDriverId) {
          driversSkipped += 1
          processedRows.push(clonedRow)
          continue
        }

        let existingDriver = driverMap.get(targetDriverId)
        if (!existingDriver) {
          const fetchedDriver = await DriverMasterService.getDriver(this.user, targetDriverId)
          if (!fetchedDriver) {
            throw new Error('Föraren kunde inte hittas för uppdatering')
          }
          existingDriver = fetchedDriver
          driverMap.set(fetchedDriver.id, fetchedDriver)
        }

        const { updates, updatedFields } = this.buildUpdateData(
          existingDriver,
          clonedRow,
          resolutionByField
        )

        if (updatedFields === 0) {
          driversSkipped += 1
          processedRows.push(clonedRow)
          continue
        }

        const updatedDriver = await DriverMasterService.editDriver(
          this.user,
          targetDriverId,
          updates
        )

        driversUpdated += 1
        fieldsUpdated += updatedFields
        driverMap.set(updatedDriver.id, updatedDriver)

        clonedRow.conflicts = clonedRow.conflicts?.map(conflict => {
          const resolution = resolutionByField.get(conflict.fieldName)
          if (resolution) {
            return {
              ...conflict,
              resolved: true,
              resolution: resolution.resolution
            }
          }
          return conflict
        }) || []

        processedRows.push(clonedRow)
      } catch (error) {
        driversSkipped += 1
        errors.push({
          rowIndex: row.rowIndex,
          driverName: row.identifiers.name,
          error: error instanceof Error ? error.message : 'Okänt fel vid uppdatering'
        })
        processedRows.push(row)
      } finally {
        onProgress?.(progress)
        if (processed === driverRows.length || processed % 5 === 0) {
          await this.firestoreService.updateDriverUploadStaging(stagingId, {
            processingProgress: progress
          })
        }
      }
    }

    const result: DriverBulkUploadResult = {
      totalRows: driverRows.length,
      driversCreated,
      driversUpdated,
      driversSkipped,
      fieldsUpdated,
      errors
    }

    await this.firestoreService.updateDriverUploadStaging(stagingId, {
      status: 'complete',
      processingProgress: null,
      uploadResult: result,
      driverUpdateRows: processedRows,
      error: errors.length > 0 ? 'Bearbetningen avslutades med vissa fel' : null
    })

    return result
  }

  // Helpers -----------------------------------------------------------------

  private extractIdentifiers(
    row: Record<string, any>,
    columnMapping: DriverColumnMapping,
    normalizedRow: Map<string, any>
  ): DriverUpdateRow['identifiers'] {
    return {
      name: this.normalizeString(this.getMappedValue(row, normalizedRow, columnMapping.identifierFields, 'driverName')),
      uuid: this.normalizeString(this.getMappedValue(row, normalizedRow, columnMapping.identifierFields, 'driverId')),
      email: this.normalizeString(this.getMappedValue(row, normalizedRow, columnMapping.identifierFields, 'email')),
      phone: this.normalizeString(this.getMappedValue(row, normalizedRow, columnMapping.identifierFields, 'phone')),
      personalCode: this.normalizeString(this.getMappedValue(row, normalizedRow, columnMapping.identifierFields, 'personalCode')),
      taxiCode: this.normalizeString(this.getMappedValue(row, normalizedRow, columnMapping.identifierFields, 'taxiCode')),
      employeeNumber: this.normalizeString(this.getMappedValue(row, normalizedRow, columnMapping.identifierFields, 'employeeNumber'))
    }
  }

  private extractProfileData(
    row: Record<string, any>,
    columnMapping: DriverColumnMapping,
    normalizedRow: Map<string, any>
  ): DriverUpdateRow['profileData'] {
    const profileFields = columnMapping.profileFields || {}
    return {
      bankAccount: this.normalizeString(this.getMappedValue(row, normalizedRow, profileFields, 'bankAccount')),
      paymentReference: this.normalizeString(this.getMappedValue(row, normalizedRow, profileFields, 'paymentReference')),
      taxTableName: this.normalizeString(this.getMappedValue(row, normalizedRow, profileFields, 'taxTableName')),
      taxColumn: this.parseNumber(this.getMappedValue(row, normalizedRow, profileFields, 'taxColumn')),
      salaryGarnishment: this.parseNumber(this.getMappedValue(row, normalizedRow, profileFields, 'salaryGarnishment'))
    }
  }

  private detectConflicts(existingDriver: DriverMaster, row: DriverUpdateRow): FieldConflict[] {
    const conflicts: FieldConflict[] = []

    const compareField = (
      fieldName: string,
      existingValue: unknown,
      newValue: unknown
    ) => {
      if (newValue === undefined || newValue === null || newValue === '') {
        return
      }

      const existingNormalized = this.normalizeComparisonValue(existingValue)
      const newNormalized = this.normalizeComparisonValue(newValue)

      if (
        existingNormalized !== undefined &&
        existingNormalized !== null &&
        existingNormalized !== newNormalized
      ) {
        conflicts.push({
          fieldName,
          existingValue,
          newValue,
          resolved: false
        })
      }
    }

    compareField('primaryName', existingDriver.primaryName, row.identifiers.name)
    compareField('email', existingDriver.email, row.identifiers.email)
    compareField('phone', existingDriver.phone, row.identifiers.phone)
    compareField('personalCode', existingDriver.personalCode, row.identifiers.personalCode)
    compareField('taxiCode', existingDriver.taxiCode, row.identifiers.taxiCode)
    compareField('employeeNumber', existingDriver.employeeNumber, row.identifiers.employeeNumber)
    compareField('bankAccount', existingDriver.bankAccount, row.profileData.bankAccount)
    compareField('paymentReference', existingDriver.paymentReference, row.profileData.paymentReference)
    compareField('taxTableName', existingDriver.taxTableName, row.profileData.taxTableName)
    compareField('taxColumn', existingDriver.taxColumn, row.profileData.taxColumn)
    compareField('salaryGarnishment', existingDriver.salaryGarnishment, row.profileData.salaryGarnishment)

    return conflicts
  }

  private buildCreateData(
    row: DriverUpdateRow,
    action: UnmatchedDriverAction
  ): CreateDriverMasterData {
    const primaryName =
      action.newDriverData?.primaryName?.trim() ||
      row.identifiers.name?.trim()

    if (!primaryName) {
      throw new Error('Namnet är obligatoriskt för nya förare')
    }

    const createData: CreateDriverMasterData = {
      primaryName
    }

    const email = action.newDriverData?.email?.trim() || row.identifiers.email || undefined
    if (email) createData.email = email

    const phone = action.newDriverData?.phone?.trim() || row.identifiers.phone || undefined
    if (phone) createData.phone = phone

    const personalCode = action.newDriverData?.personalCode?.trim() || row.identifiers.personalCode || undefined
    if (personalCode) createData.personalCode = personalCode

    if (row.identifiers.taxiCode) createData.taxiCode = row.identifiers.taxiCode.trim()
    if (row.identifiers.employeeNumber) createData.employeeNumber = row.identifiers.employeeNumber.trim()
    if (row.profileData.bankAccount) createData.bankAccount = row.profileData.bankAccount.trim()
    if (row.profileData.paymentReference) createData.paymentReference = row.profileData.paymentReference.trim()
    if (row.profileData.taxTableName) createData.taxTableName = row.profileData.taxTableName.trim()
    if (typeof row.profileData.taxColumn === 'number') createData.taxColumn = row.profileData.taxColumn
    if (typeof row.profileData.salaryGarnishment === 'number') {
      createData.salaryGarnishment = row.profileData.salaryGarnishment
    }

    return createData
  }

  private buildUpdateData(
    existingDriver: DriverMaster,
    row: DriverUpdateRow,
    resolutionByField: Map<string, FieldConflictResolution>
  ) {
    const updates: UpdateDriverMasterData = {}
    let updatedFields = 0

    const conflictLookup = new Map(
      (row.conflicts || []).map(conflict => [conflict.fieldName, conflict])
    )

    const maybeApplyField = <K extends keyof UpdateDriverMasterData>(
      fieldName: K,
      newValue: UpdateDriverMasterData[K] | undefined,
      existingValue: unknown
    ) => {
      if (newValue === undefined || newValue === null || newValue === '') {
        return
      }

      const conflict = conflictLookup.get(fieldName)
      if (conflict) {
        const resolution = resolutionByField.get(fieldName)
        // No stored resolution means the user left the default ('use_new'), so apply the update.
        // Only skip if explicitly set to keep_existing or skip.
        if (resolution && (resolution.resolution === 'keep_existing' || resolution.resolution === 'skip')) {
          return
        }
      } else {
        const existingNormalized = this.normalizeComparisonValue(existingValue)
        const newNormalized = this.normalizeComparisonValue(newValue)
        if (
          existingNormalized !== undefined &&
          existingNormalized !== null &&
          existingNormalized === newNormalized
        ) {
          return
        }
      }

      updates[fieldName] = newValue as UpdateDriverMasterData[K]
      updatedFields += 1
    }

    maybeApplyField('primaryName', row.identifiers.name, existingDriver.primaryName)
    maybeApplyField('email', row.identifiers.email, existingDriver.email)
    maybeApplyField('phone', row.identifiers.phone, existingDriver.phone)
    maybeApplyField('personalCode', row.identifiers.personalCode, existingDriver.personalCode)
    maybeApplyField('taxiCode', row.identifiers.taxiCode, existingDriver.taxiCode)
    maybeApplyField('employeeNumber', row.identifiers.employeeNumber, existingDriver.employeeNumber)
    maybeApplyField('bankAccount', row.profileData.bankAccount, existingDriver.bankAccount)
    maybeApplyField('paymentReference', row.profileData.paymentReference, existingDriver.paymentReference)
    maybeApplyField('taxTableName', row.profileData.taxTableName, existingDriver.taxTableName)
    maybeApplyField('taxColumn', row.profileData.taxColumn, existingDriver.taxColumn)
    maybeApplyField('salaryGarnishment', row.profileData.salaryGarnishment, existingDriver.salaryGarnishment)

    return {
      updates,
      updatedFields
    }
  }

  private buildProgress(
    startTime: number,
    current: number,
    total: number
  ) {
    const elapsedSeconds = (Date.now() - startTime) / 1000
    const rate = current > 0 ? elapsedSeconds / current : 0
    const remaining = total - current
    const estimatedTimeRemaining = rate > 0 ? Math.round(remaining * rate) : undefined

    return {
      current,
      total,
      percentage: Math.round((current / total) * 100),
      status: `Bearbetar rad ${current} av ${total}`,
      estimatedTimeRemaining
    }
  }

  private getMappedValue(
    row: Record<string, any>,
    normalizedRow: Map<string, any>,
    mapping: Record<string, string>,
    key: string
  ) {
    const header = mapping?.[key]
    if (!header) return undefined

    if (row.hasOwnProperty(header)) {
      return row[header]
    }

    return normalizedRow.get(this.normalizeHeaderKey(header))
  }

  private buildNormalizedRow(row: Record<string, any>): Map<string, any> {
    const normalized = new Map<string, any>()
    Object.entries(row).forEach(([key, value]) => {
      normalized.set(this.normalizeHeaderKey(key), value)
    })
    return normalized
  }

  private normalizeHeaderKey(header: string): string {
    return header
      .toLowerCase()
      .replace(/\s+/g, '')
      .replace(/[-_/\\.]/g, '')
  }

  private normalizeString(value: any): string | undefined {
    if (value === null || value === undefined) return undefined
    let stringValue = String(value).trim()

    if (stringValue.startsWith('"') && stringValue.endsWith('"')) {
      stringValue = stringValue.slice(1, -1).trim()
    }

    return stringValue.length > 0 ? stringValue : undefined
  }

  private parseNumber(value: any): number | undefined {
    if (value === null || value === undefined || value === '') {
      return undefined
    }

    if (typeof value === 'number') {
      return Number.isNaN(value) ? undefined : value
    }

    const normalized = String(value)
      .replace(',', '.')
      .replace(/\s/g, '')

    const parsed = parseFloat(normalized)
    return Number.isNaN(parsed) ? undefined : parsed
  }

  private normalizeComparisonValue(value: unknown): string | number | undefined {
    if (value === null || value === undefined || value === '') {
      return undefined
    }

    if (typeof value === 'number') {
      return value
    }

    return String(value).trim()
  }

  private countDefinedFields(data: Record<string, unknown>): number {
    return Object.entries(data).reduce((count, [key, value]) => {
      if (key === 'primaryName') return count + 1
      if (value === null || value === undefined || value === '') {
        return count
      }
      return count + 1
    }, 0)
  }
}
